import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { SocketProvider } from './context/SocketContext';
import { Loading } from './components/Common';
import Layout from './components/Layout';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Customers from './pages/Customers';
import Staff from './pages/Staff';
import Roles from './pages/Roles';
import Services from './pages/Services';
import Pricing from './pages/Pricing';
import Packages from './pages/Packages';
import Stores from './pages/Stores';
import Partners from './pages/Partners';
import LiveMap from './pages/LiveMap';
import Coupons from './pages/Coupons';
import Banners from './pages/Banners';
import Payments from './pages/Payments';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import Cms from './pages/Cms';
import Settings from './pages/Settings';
import Holidays from './pages/Holidays';
import Locations from './pages/Locations';

/** Blocks a route until the session is known, then on the required permission. */
function Guard({ permission, children }) {
  const { admin, loading, can } = useAuth();

  if (loading) return <Loading label="Checking your session" />;
  if (!admin) return <Navigate to="/login" replace />;
  if (permission && !can(permission)) {
    return (
      <div className="card">
        <div className="empty">
          <div className="strong" style={{ fontSize: 16, color: 'var(--navy)' }}>No access</div>
          <div style={{ marginTop: 8 }}>
            Your role does not include the <span className="mono">{permission}</span> permission.
            <br />Ask the Super Admin to grant it.
          </div>
        </div>
      </div>
    );
  }
  return children;
}

function Shell() {
  const { admin, loading } = useAuth();

  if (loading) return <Loading label="Starting UrbanClean" />;
  if (!admin) return <Navigate to="/login" replace />;

  return (
    <SocketProvider>
      <Layout />
    </SocketProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route element={<Shell />}>
              <Route index element={<Guard permission="dashboard.view"><Dashboard /></Guard>} />
              <Route path="orders" element={<Guard permission="order.view"><Orders /></Guard>} />
              <Route path="orders/:id" element={<Guard permission="order.view"><OrderDetail /></Guard>} />
              <Route path="customers" element={<Guard permission="customer.view"><Customers /></Guard>} />
              <Route path="staff" element={<Guard permission="staff.view"><Staff /></Guard>} />
              <Route path="roles" element={<Guard permission="role.view"><Roles /></Guard>} />
              <Route path="services" element={<Guard permission="service.view"><Services /></Guard>} />
              <Route path="pricing" element={<Guard permission="pricing.view"><Pricing /></Guard>} />
              <Route path="packages" element={<Guard permission="package.view"><Packages /></Guard>} />
              <Route path="stores" element={<Guard permission="store.view"><Stores /></Guard>} />
              <Route path="partners" element={<Guard permission="partner.view"><Partners /></Guard>} />
              <Route path="live-map" element={<Guard permission="dispatch.view"><LiveMap /></Guard>} />
              <Route path="coupons" element={<Guard permission="coupon.view"><Coupons /></Guard>} />
              <Route path="holidays" element={<Guard permission="holiday.view"><Holidays /></Guard>} />
              <Route path="locations" element={<Guard permission="location.view"><Locations /></Guard>} />
              <Route path="banners" element={<Guard permission="banner.view"><Banners /></Guard>} />
              <Route path="payments" element={<Guard permission="payment.view"><Payments /></Guard>} />
              <Route path="notifications" element={<Guard permission="notification.view"><Notifications /></Guard>} />
              <Route path="reports" element={<Guard permission="report.view"><Reports /></Guard>} />
              <Route path="cms" element={<Guard permission="cms.view"><Cms /></Guard>} />
              <Route path="settings" element={<Guard permission="settings.view"><Settings /></Guard>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
